import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, KeyRound } from "lucide-react";
import StepLayout from "./StepLayout";

/**
 * Step 1 — owner secures their account.
 *
 * If `must_reset_password` is true the user MUST set a new password
 * before continuing (no skip). Once flipped to false (either via this
 * step or earlier) we auto-mark the step done and let them move on.
 */
export default function Step1Password({ stepIndex, totalSteps, progress, onAdvance, onBack }) {
  const { user, refresh } = useAuth();
  const forced = user?.must_reset_password === true;
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [autoMarked, setAutoMarked] = useState(false);

  // Already secure? Skip straight ahead the first time we land.
  const alreadyOk = !forced && (progress?.passwd || autoMarked);

  useEffect(() => {
    // If they previously completed this step and don't need to reset,
    // mark the flag once so subsequent loads don't show this step at all.
    if (!forced && !progress?.passwd) {
      (async () => {
        try {
          await api.put("/tenant/onboarding-progress", { passwd: true });
          setAutoMarked(true);
        } catch (e) { console.debug("auto-mark passwd failed", e); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setErr("");
    if (!forced && !current && !pwd) {
      // Nothing to change — just advance
      return onAdvance({ passwd: true });
    }
    if (pwd.length < 6) return setErr("Password must be at least 6 characters.");
    if (pwd !== pwd2) return setErr("New passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      await refresh();
      await onAdvance({ passwd: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
      subtitle="Let's set up Vanguard Beacon together — it should take 10 minutes."
      description={
        <div className="space-y-3">
          <p>First things first: secure your account with a private password only you know. Your onboarding contact gave you a temporary one — we'll swap it out now.</p>
          <p className="opacity-70 text-xs">You can come back to this anytime from Settings → My Account.</p>
        </div>
      }
      illustration={
        <div className="vb-card p-4 flex items-center gap-3" style={{ background: "var(--vb-bg)" }}>
          <ShieldCheck size={28} style={{ color: "var(--vb-accent)" }} />
          <div>
            <div className="vb-heading text-sm" style={{ color: "var(--vb-text-2)" }}>Why this matters</div>
            <div className="text-xs opacity-70">ISO 22000 requires per-person accountability. Shared logins break the audit chain.</div>
          </div>
        </div>
      }
      onBack={onBack}
      onNext={submit}
      nextLabel={alreadyOk ? "Continue" : "Set password & continue"}
      nextDisabled={forced && (!current || !pwd || !pwd2)}
      busy={busy}
      errorMsg={err}
    >
      {alreadyOk ? (
        <div className="vb-card p-6 flex items-center gap-3" data-testid="passwd-already-ok">
          <ShieldCheck size={20} style={{ color: "var(--vb-accent)" }} />
          <div>
            <div className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Account already secured</div>
            <p className="text-sm opacity-70 m-0">You've already set your own password. Tap Continue to move on.</p>
          </div>
        </div>
      ) : (
        <div className="vb-card p-6 space-y-4" data-testid="passwd-form">
          <div className="flex items-center gap-2">
            <KeyRound size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Set your password</h2>
          </div>
          <div>
            <Label>Current / temporary password</Label>
            <input type="password" required autoComplete="current-password"
              className="vb-input" value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="wiz-pw-current"
              placeholder="Provided by your onboarding contact" />
          </div>
          <div>
            <Label>New password</Label>
            <input type="password" required autoComplete="new-password"
              className="vb-input" value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="wiz-pw-new"
              placeholder="At least 6 characters" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <input type="password" required autoComplete="new-password"
              className="vb-input" value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              data-testid="wiz-pw-confirm"
              placeholder="Re-enter the new password" />
          </div>
        </div>
      )}
    </StepLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
